import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMemo } from "react";

import type { ReservationOperationItem } from "@bw/shared/types/generated";
import { formatMoney } from "@bw/shared/utils";

interface DateTimeFormatters {
  date: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
}

function createFormatters(timeZone: string): DateTimeFormatters {
  return {
    date: new Intl.DateTimeFormat("en-US", {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }),
  };
}

function statusLabel(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function statusColor(
  status: string,
): "default" | "error" | "info" | "success" | "warning" {
  if (status === "confirmed") return "success";
  if (status === "completed") return "info";
  if (status === "cancelled") return "error";
  if (status === "no_show") return "warning";
  return "default";
}

interface ReservationOperationsTableProps {
  items: ReservationOperationItem[];
  total: number;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function ReservationOperationsTable({
  items,
  total,
  isLoading = false,
  emptyMessage = "No reservations match these filters.",
}: ReservationOperationsTableProps) {
  const formatters = useMemo(() => {
    const result = new Map<string, DateTimeFormatters>();
    for (const item of items) {
      if (!result.has(item.facility_timezone)) {
        result.set(
          item.facility_timezone,
          createFormatters(item.facility_timezone),
        );
      }
    }
    return result;
  }, [items]);

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      role="region"
      aria-label="Reservation results"
      tabIndex={0}
      sx={{
        overflowX: "auto",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Table
        size="small"
        aria-label={
          isLoading
            ? "Reservations loading"
            : `Reservations, showing ${items.length} of ${total}`
        }
        aria-busy={isLoading}
        sx={{ minWidth: 960 }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Player / event</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell>Facility / court</TableCell>
            <TableCell>Local time</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ height: 80 }}>
                <Typography
                  role="status"
                  aria-live="polite"
                  color="text.secondary"
                  variant="body2"
                >
                  Loading reservations...
                </Typography>
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ height: 96 }}>
                <Typography
                  role="status"
                  aria-live="polite"
                  color="text.secondary"
                  variant="body2"
                >
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => {
              const itemFormatters =
                formatters.get(item.facility_timezone) ??
                createFormatters("UTC");
              const isBlock = Boolean(item.event_name);
              const primaryLabel = isBlock
                ? item.event_name?.trim() || "Court block"
                : item.display_name?.trim() || "Reserved";

              return (
                <TableRow key={item.booking_id} hover>
                  <TableCell sx={{ minWidth: 170 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {primaryLabel}
                      </Typography>
                      {isBlock && (
                        <Chip
                          data-testid="block-badge"
                          label="Block"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    {item.organization_name}
                  </TableCell>
                  <TableCell sx={{ minWidth: 230 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.facility_name}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {item.court_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 185, whiteSpace: "nowrap" }}>
                    <Typography variant="body2">
                      {itemFormatters.date.format(new Date(item.start_time))}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {itemFormatters.time.format(new Date(item.start_time))}
                      {" – "}
                      {itemFormatters.time.format(new Date(item.end_time))}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {isBlock
                      ? "No charge"
                      : formatMoney(item.amount_charged, item.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(item.status)}
                      color={statusColor(item.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
